import React, { FC, useState } from 'react';
import ListCheckIcon from 'components/icons/list-check';
import Button from 'components/revelo-ui/content/button';
import { useFormat } from 'helpers/hooks/useFormat';
import { useAccount } from 'frontastic';
import ChevronLeftIcon from '../../../../icons/chevron-left';

export interface Props {
  onCloseContent?: (data?: any) => void;
}

const Security: FC<Props> = ({ onCloseContent }) => {
  //account data
  const { changePassword } = useAccount();
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });

  //new passwords
  const initialData = { oldPassword: '', password: '', confirmPassword: '' };
  const [data, setData] = useState(initialData);

  //error
  const [error, setError] = useState('');

  //success
  const [success, setSuccess] = useState('');

  //loading..
  const [processing, setProcessing] = useState(false);

  //input change handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  //submission handler
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    //loading starts
    setProcessing(true);
    //try updating user's password
    try {
      const response = await changePassword(data.oldPassword, data.password);
      if (response.accountId) {
        setSuccess(
          formatAccountMessage({ id: 'password.change.success', defaultMessage: 'Password successfully changed!' }),
        );
        setError('');
      } else {
        setError(
          formatAccountMessage({
            id: 'password.change.exception',
            defaultMessage: "Sorry, we couldn't fulfill your request",
          }),
        );
        setSuccess('');
      }
    } catch (err) {
      setError(formatAccountMessage({ id: 'password.change.wrong', defaultMessage: 'Wrong password entered' }));
      setSuccess('');
    } finally {
      setProcessing(false);
      setData(initialData);
    }
  };

  //save button disabled
  const submitDisabled =
    !(data.password && data.confirmPassword && data.oldPassword) || data.password !== data.confirmPassword;

  return (
    <div>
      <div className="mt-6 divide-y divide-gray-200">
        <div className="space-y-1">
          <h3 className="flex items-center gap-2 font-headline text-2xl text-neutral-950 md:text-4xl">
            <button onClick={onCloseContent} className="lg:hidden">
              <ChevronLeftIcon className="size-6" />
            </button>
            {formatAccountMessage({ id: 'change.password', defaultMessage: 'Change password' })}
          </h3>
        </div>
      </div>
      <form
        className="mt-8 flex flex-col items-start gap-4 rounded bg-neutral-50 p-4 lg:rounded-none lg:bg-transparent lg:p-0"
        onSubmit={handleSubmit}
      >
        {success && (
          <div className="flex gap-2">
            <ListCheckIcon className="size-6" />
            <span className="text-base text-neutral-950">{success}</span>
          </div>
        )}
        {error && <p className="text-base text-danger-700">{error}</p>}
        <div className="mt-3 w-full max-w-80">
          <input
            aria-label="old password"
            id="old-password"
            name="oldPassword"
            type="password"
            className="block w-full rounded border border-neutral-600 px-4 py-3.5 text-md focus:border-neutral-950 focus:ring-neutral-950"
            placeholder={formatAccountMessage({
              id: 'password.old',
              defaultMessage: 'Old password',
            })}
            required
            onChange={handleChange}
            value={data.oldPassword}
          />
        </div>
        <div className="mt-3 w-full max-w-80">
          <input
            aria-label="new password"
            id="password"
            name="password"
            type="password"
            className="block w-full rounded border border-neutral-600 px-4 py-3 text-md focus:border-neutral-950 focus:ring-neutral-950"
            placeholder={formatAccountMessage({
              id: 'password.new',
              defaultMessage: 'New Password',
            })}
            required
            onChange={handleChange}
            value={data.password}
          />
        </div>
        <div className="mb-2 mt-3 w-full max-w-80">
          <input
            aria-label="confirm new password"
            id="confirm-password"
            name="confirmPassword"
            type="password"
            className="block w-full rounded border border-neutral-600 px-4 py-3 text-md focus:border-neutral-950 focus:ring-neutral-950"
            placeholder={formatAccountMessage({
              id: 'password.new.confirm',
              defaultMessage: 'Confirm new password',
            })}
            required
            onChange={handleChange}
            value={data.confirmPassword}
          />
        </div>
        <Button type="submit" style="primary" disabled={submitDisabled || processing} className="w-full lg:w-auto">
          {formatAccountMessage({ id: 'change.password', defaultMessage: 'Change password' })}
        </Button>
      </form>
    </div>
  );
};

export default Security;
